import { FC, useState } from 'react'
import { Button } from 'primereact/button'
import { InputTextarea } from 'primereact/inputtextarea'
import Modal from '../Modal'

type SimChangeReasonModalProps = {
  previousSimFile: string
  newSimFile: string
  onConfirm: (reason: string) => void
  onCancel: () => void
}

/**
 * Modal that requires the user to provide a reason when changing a SIM file.
 * Upload/replacement cannot proceed without a non-empty reason.
 */
const SimChangeReasonModal: FC<SimChangeReasonModalProps> = ({
  previousSimFile,
  newSimFile,
  onConfirm,
  onCancel
}) => {
  const [reason, setReason] = useState('')
  const [touched, setTouched] = useState(false)

  const isValid = reason.trim().length > 0

  const handleConfirm = () => {
    setTouched(true)
    if (isValid) {
      onConfirm(reason.trim())
    }
  }

  return (
    <Modal
      title="SIM File Change — Reason Required"
      isOpen={true}
      withAction={false}
      size="md"
      easyClose={false}
      zIndex={40}
      onClose={onCancel}
      onCancel={onCancel}>
      <div className="flex flex-col gap-4 pt-2">
        <p className="text-sm text-gray-600 dark:text-gray-300">
          You are changing the SIM template for this case. A reason is required to proceed.
        </p>

        <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
          {previousSimFile && (
            <div>
              <span className="font-semibold">Previous:</span> {previousSimFile}
            </div>
          )}
          <div>
            <span className="font-semibold">New:</span> {newSimFile}
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Reason for change <span className="text-red-500">*</span>
          </label>
          <InputTextarea
            value={reason}
            onChange={e => {
              setReason(e.target.value)
              if (!touched) setTouched(true)
            }}
            rows={3}
            placeholder="Describe why the SIM file is being changed..."
            className={touched && !isValid ? 'p-invalid' : ''}
            autoFocus
          />
          {touched && !isValid && (
            <small className="text-red-500">A reason is required to change the SIM file.</small>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Button label="Cancel" className="p-button-text p-button-sm" onClick={onCancel} />
          <Button
            label="Confirm Change"
            icon="pi pi-check"
            className="p-button-sm"
            onClick={handleConfirm}
            disabled={!isValid}
          />
        </div>
      </div>
    </Modal>
  )
}

export default SimChangeReasonModal
